import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { adminApi } from "../../repositories/admin";
import { Check } from "lucide-react";
import Button from "../../components/ui/Button";
import { Skeleton, SkeletonText } from "../../components/ui/skeleton";

interface Permission {
  limit: number | null;
  permission_id: number;
  permission_name: string;
}

interface PackageDetails {
  name: string;
  description: string;
  prices: Array<{
    unit_amount: number;
    recurring: {
      interval: string;
    };
  }>;
  permissions?: Permission[];
}

type BillingCycle = "package1" | "package2";

const StripeUpgrade: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [packageIds, setPackageIds] = useState<{ packageId1: string; packageId2: string } | null>(null);
  const [userInfo, setUserInfo] = useState<{ email: string; userId: string } | null>(null);
  const [package1, setPackage1] = useState<PackageDetails | null>(null);
  const [package2, setPackage2] = useState<PackageDetails | null>(null);
  const [selectedCycle, setSelectedCycle] = useState<BillingCycle>("package1");
  const [redirecting, setRedirecting] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const monthlyPackage = package1?.prices[0]?.recurring?.interval === "month" ? package1 : package2;
  const yearlyPackage = package1?.prices[0]?.recurring?.interval === "year" ? package1 : package2;
  const monthlyPackageId = package1?.prices[0]?.recurring?.interval === "month" ? "package1" : "package2";
  const yearlyPackageId = package1?.prices[0]?.recurring?.interval === "year" ? "package1" : "package2";

  useEffect(() => {
    if (package1 && package2 && !isInitialized) {
      setSelectedCycle(monthlyPackageId);
      setIsInitialized(true);
    }
  }, [package1, package2, monthlyPackageId, isInitialized]);

  useEffect(() => {
    const validateToken = async () => {
      const token = searchParams.get("token");

      if (!token) {
        setError("No token provided");
        setLoading(false);
        return;
      }

      try {
        const response = await adminApi.validateToken(token);

        if (response.data?.data?.packageId1 && response.data?.data?.packageId2) {
          setPackageIds({
            packageId1: response.data.data.packageId1,
            packageId2: response.data.data.packageId2,
          });

          if (response.data.data.email && response.data.data.userId) {
            setUserInfo({
              email: response.data.data.email,
              userId: response.data.data.userId,
            });
          } else {
            setUserInfo({
              email: "",
              userId: "",
            });
          }
        } else {
          setError("Invalid response from server");
          setLoading(false);
        }
      } catch (err: any) {
        setError(err.response?.data?.message || "Failed to validate token");
        setLoading(false);
      }
    };

    validateToken();
  }, [searchParams]);

  useEffect(() => {
    const fetchPackageDetails = async () => {
      if (!packageIds) return;

      try {
        const [package1Response, package2Response] = await Promise.all([
          adminApi.getPackageDetails(packageIds.packageId1),
          adminApi.getPackageDetails(packageIds.packageId2),
        ]);

        if (package1Response.data?.success && package1Response.data?.data) {
          setPackage1(package1Response.data.data);
        }

        if (package2Response.data?.success && package2Response.data?.data) {
          setPackage2(package2Response.data.data);
        }

        setLoading(false);
      } catch (err: any) {
        setError(err.response?.data?.message || "Failed to fetch package details");
        setLoading(false);
      }
    };

    fetchPackageDetails();
  }, [packageIds]);

  const handleBuyNow = async () => {
    if (!packageIds || !userInfo) return;

    const selectedPackageId = selectedCycle === "package1" ? packageIds.packageId1 : packageIds.packageId2;
    const token = searchParams.get("token");

    try {
      setRedirecting(true);
      const response = await adminApi.getCheckoutUrl(
        selectedPackageId,
        userInfo.email,
        userInfo.userId,
        token || undefined
      );

      if (response.data?.success && response.data?.data?.checkoutUrl) {
        // Paid package: redirect to Stripe Checkout.
        window.location.href = response.data.data.checkoutUrl;
      } else if (response.data?.success && response.data?.data?.isFree) {
        // Free ($0) package: no card needed. The backend already created the
        // Stripe subscription directly, so send the user straight to success.
        window.location.href = "/success?free=true";
      } else {
        setError("Failed to get checkout URL");
        setRedirecting(false);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to initiate checkout");
      setRedirecting(false);
    }
  };

  const selectedPackage = selectedCycle === monthlyPackageId ? monthlyPackage : yearlyPackage;

  const formatPermissionName = (permissionName: string): string => {
    const parts = permissionName.split(".");
    if (parts.length === 2) {
      const [feature, action] = parts;
      return `${feature.charAt(0).toUpperCase() + feature.slice(1)} ${action}`;
    }
    return permissionName;
  };

  const getPermissionDisplay = (permission: Permission): string => {
    const name = formatPermissionName(permission.permission_name);
    if (permission.limit === null) {
      return `Unlimited ${name}`;
    } else if (permission.limit === -1) {
      return `Unlimited ${name}`;
    } else {
      return `${permission.limit} ${name}`;
    }
  };

  // Shaped like the real page (nav, heading, cycle toggle, plan card) so the
  // plans drop into place instead of replacing a centred spinner.
  const pageSkeleton = (
    <div className="min-h-screen bg-neutral-50" aria-busy="true">
      <nav className="bg-black">
        <div className="max-w-6xl px-6 py-4 mx-auto">
          <img src="/images/logo.png" alt="Cadence IO" className="h-12" />
        </div>
      </nav>
      <div className="max-w-6xl px-4 py-12 mx-auto">
        <Skeleton className="h-7 mx-auto mb-3 w-80 max-w-full" />
        <Skeleton className="w-64 h-4 mx-auto mb-8" />
        <Skeleton className="h-12 mx-auto mb-8 rounded-lg w-60" />
        <div className="max-w-md p-6 mx-auto space-y-4 bg-white border rounded-lg border-neutral-200">
          <Skeleton className="w-40 h-6" />
          <Skeleton className="w-24 h-9" />
          <SkeletonText lines={5} />
          <Skeleton className="w-full h-10" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return pageSkeleton;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4 bg-neutral-50">
        <div className="w-full max-w-md p-8 bg-white border rounded-lg border-neutral-200">
          <div className="space-y-4 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto bg-danger-100 rounded-full">
              <svg
                className="w-8 h-8 text-danger-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-black">{error}</h2>
          </div>
        </div>
      </div>
    );
  }

  if (!package1 || !package2 || !monthlyPackage || !yearlyPackage) {
    return pageSkeleton;
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <nav className="bg-black">
        <div className="max-w-6xl px-6 py-4 mx-auto">
          <img src="/images/logo.png" alt="Cadence IO" className="h-12" />
        </div>
      </nav>

      <div className="max-w-6xl px-4 py-12 mx-auto">
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-2xl font-semibold tracking-tight text-black">Choose Your Subscription Plan</h1>
          <p className="text-sm text-neutral-600">Select a billing cycle that works best for you</p>
        </div>

        <div className="flex justify-center mb-8">
          <div className="inline-flex p-1 bg-white border rounded-lg border-neutral-200">
            <button
              onClick={() => setSelectedCycle(monthlyPackageId)}
              disabled={redirecting}
              className={`px-8 py-3 rounded-md font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                selectedCycle === monthlyPackageId
                  ? "bg-primary-700 text-white"
                  : "text-neutral-600 hover:text-primary-700"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setSelectedCycle(yearlyPackageId)}
              disabled={redirecting}
              className={`px-8 py-3 rounded-md font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                selectedCycle === yearlyPackageId
                  ? "bg-primary-700 text-white"
                  : "text-neutral-600 hover:text-primary-700"
              }`}
            >
              Yearly
            </button>
          </div>
        </div>

        <div className="max-w-sm mx-auto">
          <div className="overflow-hidden bg-white border rounded-2xl border-neutral-200">
            {/* Header Section */}
            <div className="px-8 pt-8 pb-6 text-center border-b border-neutral-200">
              <h3 className="mb-6 text-base font-semibold text-black">{selectedPackage?.name}</h3>

              {/* Price */}
              <div className="mb-1">
                <span className="text-5xl font-bold text-black">
                  ${((selectedPackage?.prices[0]?.unit_amount ?? 0) / 100).toFixed(0)}
                </span>
              </div>
              <div className="mb-6 text-xs tracking-wide uppercase text-neutral-500">
                USD/{selectedPackage?.prices[0]?.recurring?.interval}
              </div>

              {/* Buy Now Button */}
              <Button
                onClick={handleBuyNow}
                disabled={redirecting}
                className="w-full"
              >
                {redirecting ? (
                  <span className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white rounded-full border-t-transparent animate-spin"></div>
                    <span className="ml-2">Redirecting...</span>
                  </span>
                ) : selectedPackage?.prices[0]?.unit_amount ? (
                  "Buy Now"
                ) : (
                  "Get Started"
                )}
              </Button>
            </div>

            {/* Features Section */}
            <div className="px-6 py-6">
              <h4 className="mb-4 text-xs font-semibold tracking-wide uppercase text-neutral-500">
                FEATURES
              </h4>
              <ul className="space-y-3">
                {selectedPackage?.permissions && selectedPackage.permissions.length > 0 ? (
                  selectedPackage.permissions.map((permission, index) => (
                    <li key={index} className="flex items-start">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="flex items-center justify-center w-4 h-4 rounded-full bg-success-600">
                          <Check className="w-3 h-3 text-white stroke-[3]" />
                        </div>
                      </div>
                      <span className="ml-3 text-sm leading-tight text-neutral-700">
                        {getPermissionDisplay(permission)}
                      </span>
                    </li>
                  ))
                ) : (
                  <>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="flex items-center justify-center w-4 h-4 rounded-full bg-success-600">
                          <Check className="w-3 h-3 text-white stroke-[3]" />
                        </div>
                      </div>
                      <span className="ml-3 text-sm leading-tight text-neutral-700">Full access to all features</span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="flex items-center justify-center w-4 h-4 rounded-full bg-success-600">
                          <Check className="w-3 h-3 text-white stroke-[3]" />
                        </div>
                      </div>
                      <span className="ml-3 text-sm leading-tight text-neutral-700">Priority support</span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="flex items-center justify-center w-4 h-4 rounded-full bg-success-600">
                          <Check className="w-3 h-3 text-white stroke-[3]" />
                        </div>
                      </div>
                      <span className="ml-3 text-sm leading-tight text-neutral-700">Cancel anytime</span>
                    </li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StripeUpgrade;
